import React, { useEffect, useMemo, useRef, useState } from 'react'

import { animated, config, useSpring, useTransition } from '@react-spring/web'
import { useAccount, useProvider, useSigner } from 'wagmi'
import { formatEther, formatUnits, parseUnits } from '@ethersproject/units'

import { HeaderSpacer } from 'components/core/header/Header'
import { PageView } from 'components/core/layout/FeaturedItemPage'
import GooTopContainer from 'components/factory/GooTopContainer'
import NoGobblersView from 'components/factory/NoGobblersView'
import { Spacer } from 'components/core/Spacer'
import LoadingSpinner from 'components/core/LoadingSpinner'
import GobblerTable from 'components/factory/GobblerTable'
import GobblerMobileTable from 'components/factory/GobblerMobileTable'

import useIsMobile from 'hooks/util/useIsMobile'
import { useIsMounted } from 'hooks/util/useIsMounted'
import { useNftsInWalletByCollection } from 'hooks/nfts/useNftsInWallet'
import { useContractAddresses } from 'hooks/gobblers/useContractAddresses'
import { useSmartContractReadCall } from 'hooks/react-query-typechain/useSmartContractReadCall'
import { useInterval } from 'hooks/util/useInterval'
import { useSmartContractTransaction } from 'hooks/react-query-typechain/useSmartContractTransaction'

import { calculateGooBalanceFromSnapshot, calculateGooProductionRate } from 'utils/emissions'

import { DEFAULT_CHAIN_ID } from 'config'
import { ArtGobblers__factory } from 'contracts'
import { useRouter } from 'next/router'
import useContracts from 'hooks/mint/useContracts'

import DepositGooModal from 'components/modal/DepositGooModal'
import { sleep } from 'utils/sleep'

const AnimatedPageView = animated(PageView)

function useGooSpringInfo() {
  const chainId = DEFAULT_CHAIN_ID
  const { address: walletAddress } = useAccount()
  const { data: signer } = useSigner()
  const provider = useProvider()
  const addresses = useContractAddresses(DEFAULT_CHAIN_ID)

  const [artGobblersContract, gooContract] = useContracts(chainId)

  const gooBalanceQuery = useSmartContractReadCall(artGobblersContract, 'gooBalance', {
    callArgs: [walletAddress!],
    enabled: !!walletAddress,
  })

  const artGobblersContractAddress = addresses?.ART_GOBBLERS
  const artGobblersReadContract = useMemo(() => {
    return artGobblersContractAddress
      ? ArtGobblers__factory.connect(artGobblersContractAddress, provider)
      : undefined
  }, [artGobblersContractAddress, provider])

  const gobblerUserData = useSmartContractReadCall(artGobblersReadContract, 'getUserData', {
    callArgs: [walletAddress!],
    enabled: !!walletAddress,
  })

  const [virtualBalanceSpring, virtualBalanceSpringApi] = useSpring(() => ({
    amount: 0,
  }))

  const gooVirtualBalanceRef = useRef<number>(0)
  const [virtualBalanceTrigger, setVirtualBalanceTrigger] = useState<number | undefined>(undefined)

  const [userGobblersOwnedAmount, userEmissionMultiple, userLastBalance, lastTimestamp] =
    gobblerUserData.data ?? [undefined, undefined, undefined, undefined]

  const [initialVirtualBalanceOnPageLoad, setInitialVirtualBalanceOnPageLoad] = useState<
    number | undefined
  >()

  const removeGooMutation = useSmartContractTransaction(
    artGobblersReadContract,
    'removeGoo',
    signer ?? undefined,
  )

  const gooBalance = gooBalanceQuery.data ? formatEther(gooBalanceQuery.data) : '0'

  useInterval(() => {
    if (gobblerUserData.isLoading) {
      return
    }
    const computeVirtualBalance = () => {
      // const gooBalance = gooBalanceQuery.data ? formatEther(gooBalanceQuery.data) : '0'

      // const gooVirtualBalance = calculateGooBalanceFromSnapshot(
      //   BigInt(userLastBalance?.toString() ?? 0),
      //   lastTimestamp?.toNumber() ?? 0,
      //   userEmissionMultiple ?? 0,
      // )

      const virtualBalance = parseFloat(gooBalance)

      virtualBalanceSpringApi.start({
        amount: virtualBalance,
        config: {
          duration: gooVirtualBalanceRef.current === 0 && virtualBalance > 0 ? 2000 : 15000,
        },
      })

      gooVirtualBalanceRef.current = virtualBalance

      setVirtualBalanceTrigger(virtualBalance)

      gooBalanceQuery.refetch()
    }

    computeVirtualBalance()
    if (!initialVirtualBalanceOnPageLoad) {
      setInitialVirtualBalanceOnPageLoad(gooVirtualBalanceRef.current)
    }
    virtualBalanceSpringApi.start({
      amount: gooVirtualBalanceRef.current,
    })
  }, 2000)

  const handleRemoveGoo = async () => {
    if (!virtualBalanceTrigger) {
      return
    }
    await removeGooMutation.mutateAsync([parseUnits(virtualBalanceTrigger.toString(), '18')])

    gooBalanceQuery.refetch()
  }

  const gooInTank = parseFloat(gooBalance) //initialVirtualBalanceOnPageLoad ?? 0
  const multiplier = userEmissionMultiple ?? 0
  const gooProductionRate = calculateGooProductionRate(gooInTank, multiplier)
  const isLoading = gobblerUserData.isLoading || virtualBalanceTrigger === undefined
  return {
    virtualBalanceSpring,
    gooInTank,
    virtualBalanceTrigger,
    gooProductionRate: gooProductionRate.toFixed(4),
    multiplier,
    isLoading,
    handleRemoveGoo,
    // handleAddGoo,
    isFetched: gobblerUserData.isFetched,
    userGobblersOwnedAmount,
  }
}

export default function FactoryPage() {
  const provider = useProvider()

  const didInit = useIsMounted()
  const pageViewSpring = useSpring({
    blur: didInit ? '0px' : '2px',
    opacity: didInit ? '1' : '0.8',
  })

  const isMobile = useIsMobile()

  const accountData = useAccount()
  const walletAddress = accountData.address

  const chainId = DEFAULT_CHAIN_ID
  const addresses = useContractAddresses(chainId)

  const artGobblersContractAddress = addresses?.ART_GOBBLERS

  const artGobblersReadContract = useMemo(() => {
    return artGobblersContractAddress
      ? ArtGobblers__factory.connect(artGobblersContractAddress, provider)
      : undefined
  }, [artGobblersContractAddress, provider])

  const gobblersOwnedQuery = useNftsInWalletByCollection(
    walletAddress,
    artGobblersReadContract?.address,
    chainId,
  )

  const [artGobblersContract, gooContract] = useContracts(chainId)

  const gobblersOwned = gobblersOwnedQuery.data

  const gobblerIdsOwned = useMemo(
    () =>
      gobblersOwned?.ownedNfts.map((nft) => {
        return parseInt(nft.id.tokenId)
      }),
    [gobblersOwned?.ownedNfts],
  )

  const {
    gooInTank,
    virtualBalanceSpring,
    virtualBalanceTrigger,
    multiplier,
    handleRemoveGoo,
    // handleAddGoo,
    isLoading,
    isFetched,
    userGobblersOwnedAmount,
  } = useGooSpringInfo()

  const [gooAmountToDepositInput, setGooAmountToDepositInput] = useState<string>('0')

  const gooVirtualBalanceQuery = useSmartContractReadCall(artGobblersContract, 'gooBalance', {
    callArgs: [walletAddress!],
    enabled: !!walletAddress,
  })

  // Goo Balance
  const gooPhysicalBalanceResult = useSmartContractReadCall(gooContract, 'balanceOf', {
    callArgs: [walletAddress as string], // safe to cast because `enabled` is set
    enabled: !!walletAddress,
  })
  const gooPhysicalBalance = gooPhysicalBalanceResult.data
    ? formatUnits(gooPhysicalBalanceResult.data, 18)
    : undefined

  const handleAddGoo = async () => {
    if (!virtualBalanceTrigger) {
      return
    }
    setIsModalOpen(true)
    if (gooPhysicalBalance) {
      setGooAmountToDepositInput(gooPhysicalBalance)
    }
  }
  const { data: signer } = useSigner()

  const addGooMutation = useSmartContractTransaction(
    artGobblersReadContract,
    'addGoo',
    signer ?? undefined,
  )

  const handleAddGooClick = async () => {
    await addGooMutation.mutateAsync([parseUnits(gooAmountToDepositInput.toString(), '18')])

    gooPhysicalBalanceResult.refetch()
    gooVirtualBalanceQuery.refetch()
  }

  const showMain = isFetched
  const showNoGooView = !virtualBalanceTrigger
  const showNoGobblersView = !userGobblersOwnedAmount
  const GobblerView = isMobile ? GobblerMobileTable : GobblerTable

  const router = useRouter()
  useEffect(() => {
    if (accountData.isDisconnected && !accountData.isConnecting) {
      router.push({
        pathname: '/dashboard',
      })
    }
  }, [accountData.isConnecting, accountData.isDisconnected, router])

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const dialogTransitions = useTransition(isModalOpen, {
    from: {
      opacity: 0,
      y: -10,
      // Scale is for goo bg
      scale: 0.3,
    },
    enter: {
      opacity: 1,
      y: 0,
      scale: 1,
      config: (key: string) => {
        const clamp = key === 'scale' ? false : true
        return {
          ...config.wobbly,
          clamp,
        }
      },
    },
    leave: {
      opacity: 0,
      y: 10,
      scale: 0.8,
      config: {
        clamp: true,
      },
    },
    config: config.wobbly,
  })

  return (
    <AnimatedPageView
      key={'page-view'}
      style={{
        opacity: pageViewSpring.opacity,
        filter: pageViewSpring.blur.interpolate((value) => `blur(${value ?? 0})`),
      }}
    >
      <HeaderSpacer />
      {dialogTransitions((styles, item) => {
        if (item) {
          return (
            <DepositGooModal
              gooAmountToDepositInput={gooAmountToDepositInput}
              gooPhysicalBalance={gooPhysicalBalance}
              setGooAmountToDepositInput={setGooAmountToDepositInput}
              handleAddGooClick={handleAddGooClick}
              setIsModalOpen={setIsModalOpen}
              styles={styles}
            />
          )
        }

        return null
      })}

      {isLoading && <LoadingSpinner />}
      {!isLoading && showMain && (
        <>
          <GooTopContainer
            handleWithdrawGoo={handleRemoveGoo}
            handleOpenDepositGooModal={handleAddGoo}
            emissionsMultiple={multiplier}
            gooVirtualBalanceOnPageLoad={gooInTank}
            gooSpring={virtualBalanceSpring}
            showNoGooView={showNoGooView}
            gobblerIdsOwned={gobblerIdsOwned}
          />
          <Spacer size={32} />
          {showNoGobblersView ? (
            <NoGobblersView />
          ) : (
            <GobblerView gobblerIdsOwned={gobblerIdsOwned} virtualGooBalanceForUser={gooInTank} />
          )}
          <Spacer size={64} />
        </>
      )}
    </AnimatedPageView>
  )
}
